import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { isAlertActive, formatAnnouncementDate } from '@/lib/announcements';
import {
  getAccountAlerts,
  dismissAccountAlert,
  type AccountAlert,
} from '@/services/accountAlerts.service';
import { toast } from '@/store/toastStore';
import { AlertDetailModal } from './AlertDetailModal';
import { AlertHeroCard } from './AlertHeroCard';

export function AlertsBanner() {
  const { user } = useAuth();
  const userTz = useUserTimezone();
  const queryClient = useQueryClient();
  const [selectedAlert, setSelectedAlert] = useState<AccountAlert | null>(null);
  const [index, setIndex] = useState(0);

  const { data: alerts = [] } = useQuery({
    queryKey: ['account-alerts'],
    queryFn: getAccountAlerts,
    staleTime: 60_000,
  });

  const dismissMutation = useMutation({
    mutationFn: (id: number) => dismissAccountAlert(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account-alerts'] }),
    onError: () => toast.error('Could not dismiss the announcement. Please try again.'),
  });

  const visibleAlerts = useMemo(() => {
    if (!user) return [];
    return alerts.filter((alert) => {
      if (!alert.active) return false;
      if (alert.dismissedAt) return false; // already closed by this user
      if (user.userLevel < alert.userLevel) return false;
      if (!isAlertActive(alert, userTz)) return false;
      return true;
    });
  }, [alerts, user, userTz]);

  // Keep the active index in range as alerts are dismissed / reloaded.
  useEffect(() => {
    setIndex((i) => Math.min(i, Math.max(0, visibleAlerts.length - 1)));
  }, [visibleAlerts.length]);

  if (visibleAlerts.length === 0) return null;

  const total = visibleAlerts.length;
  const alert = visibleAlerts[Math.min(index, total - 1)];
  const dateLabel = formatAnnouncementDate(alert);

  return (
    <>
      {/* The gutter tracks the sibling content block in DashboardPage, which is on
          the md gate - the banner and the cards below it have to line up at every
          width, so this one follows that boundary rather than the card's own. */}
      <div className="px-6 pt-4 pb-1 max-md:px-4">
        <AlertHeroCard
          alert={alert}
          dateLabel={dateLabel}
          onDismiss={() => dismissMutation.mutate(alert.accountAlertID)}
          dismissDisabled={dismissMutation.isPending}
          pager={{
            index: Math.min(index, total - 1),
            total,
            onPrev: () => setIndex((i) => Math.max(0, i - 1)),
            onNext: () => setIndex((i) => Math.min(total - 1, i + 1)),
          }}
          onOpenDetail={() => setSelectedAlert(alert)}
        />

        <div className="mt-1 flex justify-end">
          {/* An 11px inline link is not a tap target. touch-target-h is a min-height,
              which needs a box that is not inline - hence inline-flex below lg. */}
          <Link
            to="/announcements"
            className="text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline max-lg:inline-flex max-lg:items-center max-lg:text-xs touch-target-h"
          >
            View all announcements
          </Link>
        </div>
      </div>

      <AlertDetailModal alert={selectedAlert} onClose={() => setSelectedAlert(null)} />
    </>
  );
}
